import { clamp } from "@microsoft/fast-colors";
import { binarySearch } from "./utilities/binary-search.js";
import { directionByIsDark } from "./utilities/direction-by-is-dark.js";
import { contrast } from "./utilities/relative-luminance.js";
/**
 * Directional values for navigating {@link Swatch}es in {@link Palette}.
 *
 * @public
 */
export const PaletteDirectionValue = Object.freeze({
    /**
     * Move darker, or up the Palette.
     */
    darker: 1,
    /**
     * Move lighter, or down the Palette.
     */
    lighter: -1,
});
/**
 * Gets a fixed {@link (PaletteDirectionValue:variable)} from {@link PaletteDirection} which may be a function that needs to be resolved.
 *
 * @param direction - A fixed palette direction value or a function that resolves to one
 * @returns A fixed palette direction value
 *
 * @public
 */
export function resolvePaletteDirection(direction) {
    if (typeof direction === "function") {
        return direction();
    }
    else {
        return direction;
    }
}
/**
 * A base {@link Palette} with a common implementation of the interface. Use PaletteRGB for an implementation
 * of a palette generation algorithm that is ready to be used directly, or extend this class to generate custom Swatches.
 *
 * @public
 */
export class BasePalette {
    /**
     * Creates a new Palette.
     *
     * @param source - The source color for the Palette
     * @param swatches - All Swatches in the Palette
     */
    constructor(source, swatches) {
        /**
         * Cache from `relativeLuminance` to `Swatch` index in the `Palette`.
         */
        this.closestIndexCache = new Map();
        this.source = source;
        this.swatches = swatches;
        this.reversedSwatches = Object.freeze([...this.swatches].reverse());
        this.lastIndex = this.swatches.length - 1;
    }
    /**
     * {@inheritdoc Palette.colorContrast}
     */
    colorContrast(reference, contrastTarget, initialSearchIndex, direction = directionByIsDark(reference)) {
        if (initialSearchIndex === undefined) {
            initialSearchIndex = this.closestIndexOf(reference);
        }
        let source = this.swatches;
        const endSearchIndex = this.lastIndex;
        let startSearchIndex = initialSearchIndex;
        const condition = (value) => contrast(reference, value) >= contrastTarget;
        if (direction === PaletteDirectionValue.lighter) {
            source = this.reversedSwatches;
            startSearchIndex = endSearchIndex - startSearchIndex;
        }
        return binarySearch(source, condition, startSearchIndex, endSearchIndex);
    }
    /**
     * {@inheritdoc Palette.delta}
     */
    delta(reference, delta, direction) {
        const dir = resolvePaletteDirection(direction);
        return this.get(this.closestIndexOf(reference) + dir * delta);
    }
    /**
     * {@inheritdoc Palette.closestIndexOf}
     */
    closestIndexOf(reference) {
        if (this.closestIndexCache.has(reference.relativeLuminance)) {
            /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
            return this.closestIndexCache.get(reference.relativeLuminance);
        }
        let index = this.swatches.indexOf(reference);
        if (index !== -1) {
            this.closestIndexCache.set(reference.relativeLuminance, index);
            return index;
        }
        const closest = this.swatches.reduce((previous, next) => Math.abs(next.relativeLuminance - reference.relativeLuminance) <
            Math.abs(previous.relativeLuminance - reference.relativeLuminance)
            ? next
            : previous);
        index = this.swatches.indexOf(closest);
        this.closestIndexCache.set(reference.relativeLuminance, index);
        return index;
    }
    /**
     * {@inheritdoc Palette.get}
     */
    get(index) {
        return this.swatches[index] || this.swatches[clamp(index, 0, this.lastIndex)];
    }
}
